import { useState, useEffect } from "react"
import { Loader2, TrendingUp, Medal, Activity, Calendar, Clock, CheckCircle2, XCircle, MessageSquare } from "lucide-react"
import { Link } from "../lib/router"
import SwimmerSidebar from "../components/SwimmerSidebar"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function SwimmerDashboard() {
  const [stats, setStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [membershipStatus, setMembershipStatus] = useState(null)
  const [showApprovedNotice, setShowApprovedNotice] = useState(false)
  const { t } = useLanguage()
  const [uploadFailures] = useState(() => {
    const cert = sessionStorage.getItem("certUploadFailed") === "1"
    const photo = sessionStorage.getItem("photoUploadFailed") === "1"
    if (cert) sessionStorage.removeItem("certUploadFailed")
    if (photo) sessionStorage.removeItem("photoUploadFailed")
    return { cert, photo }
  })

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchStats() {
      setIsLoading(true)
      try {
        const res = await fetch(`${API}/api/me/quick-stats`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch stats")
        setStats(await res.json())
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchStats()
  }, [])

  useEffect(() => {
    async function fetchMembership() {
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: authHeaders() })
        if (!res.ok) return
        const data = await res.json()
        setMembershipStatus(data.status)
        if (data.status === "approved" && !localStorage.getItem("approvedNoticeSeen")) {
          setShowApprovedNotice(true)
          localStorage.setItem("approvedNoticeSeen", "1")
        }
      } catch { /* non-critical */ }
    }
    fetchMembership()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("dash_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("dash_subtitle")}</p>
          </div>
        </header>

        {membershipStatus === "pending" && (
          <div className="mb-6 flex items-start gap-3 border border-gold-500/30 bg-gold-500/10 px-4 py-3">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" />
            <p className="text-sm font-medium text-gold-300">
              {t("dash_pending_notice")}{" "}
              <Link to="/swimmer/profile" className="underline underline-offset-2 hover:text-gold-200">{t("dash_pending_profile")}</Link>.
            </p>
          </div>
        )}

        {membershipStatus === "rejected" && (
          <div className="mb-6 flex items-start gap-3 border border-red-500/30 bg-red-500/10 px-4 py-3">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden="true" />
            <p className="text-sm font-medium text-red-300">
              {t("dash_rejected_notice")}{" "}
              <Link to="/swimmer/profile" className="underline underline-offset-2 hover:text-red-200">{t("dash_pending_profile")}</Link>.
            </p>
          </div>
        )}

        {showApprovedNotice && (
          <div className="mb-6 flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
            <p className="text-sm font-medium text-emerald-300">{t("dash_approved")}</p>
          </div>
        )}

        {(uploadFailures.cert || uploadFailures.photo) && (
          <div role="alert" className="mb-6 border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
            {uploadFailures.cert && uploadFailures.photo
              ? t("dash_upload_fail_both")
              : uploadFailures.cert
              ? t("dash_upload_fail_cert")
              : t("dash_upload_fail_photo")}{" "}
            {uploadFailures.cert && uploadFailures.photo ? t("dash_upload_fail_add_them") : t("dash_upload_fail_add_it")} {" "}
            <Link to="/swimmer/profile" className="underline underline-offset-2 hover:text-red-200">{t("dash_upload_fail_profile_link")}</Link>.
          </div>
        )}

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : stats ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={<TrendingUp className="text-gold-400" />} label={t("dash_weekly_distance")} value={stats.weekly_distance} />
            <StatCard icon={<Activity className="text-gold-400" />} label={t("dash_streak")} value={stats.current_streak} sub={t("dash_workouts")} />
            <StatCard
              icon={<Medal className="text-gold-400" />}
              label={t("dash_best_event")}
              value={stats.best_event}
              sub={`Time: ${stats.best_time}`}
              badge={stats.best_rank ? (
                <div className="flex items-center gap-1.5 rounded-lg bg-gold-500/10 px-2.5 py-1 text-xs font-bold text-gold-400 border border-gold-500/20 backdrop-blur-sm">
                  <Medal className="h-3 w-3" />
                  #{stats.best_rank}
                </div>
              ) : null}
            />
            <StatCard icon={<Calendar className="text-gold-400" />} label={t("dash_sessions")} value={`${stats.sessions_completed} / ${stats.sessions_planned}`} />
          </div>
        ) : null}

        {stats?.recent_notes && stats.recent_notes.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-5 font-display text-xl font-bold text-paper-0 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gold-400" />
              {t("dash_coach_notes")}
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {stats.recent_notes.map(note => (
                <div key={note.id} className="rounded-xl border border-white/10 bg-ink-800/30 p-5 shadow-sm relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gold-500/50"></div>
                  <p className="text-sm text-paper-0 whitespace-pre-wrap leading-relaxed">{note.note}</p>
                  <p className="mt-4 text-[10px] uppercase tracking-widest font-bold text-mist-500">
                    {note.created_at ? new Date(note.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : t("dash_recently")}

                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function StatCard({ icon, label, value, sub, badge }) {
  return (
    <div className="flex flex-col justify-center rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm relative">
      {badge && <div className="absolute top-5 right-5">{badge}</div>}
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/5">
        {icon}
      </div>
      <p className="text-sm font-semibold uppercase tracking-wider text-mist-400">{label}</p>
      <div className="mt-2 flex items-start sm:items-baseline gap-2">
        <p 
          title={typeof value === 'string' && value.length > 20 ? value : undefined}
          className={`font-display font-bold text-paper-0 ${
            typeof value === 'string' && value.length > 30 ? 'text-lg line-clamp-3' : 
            typeof value === 'string' && value.length > 15 ? 'text-xl line-clamp-2' : 
            'text-3xl'
          }`}
        >
          {value}
        </p>
        {sub && <p className="text-xs font-medium text-mist-500 shrink-0 mt-1 sm:mt-0">{sub}</p>}
      </div>
    </div>
  )
}
